import {
  createDatabase,
  DatabaseInterface,
  isWebPlatform,
} from './DatabaseInterface';
import { CREATE_TABLES_SQL } from './schema';
import { logger } from '../../utils/logger';

/**
 * SQLite Database Manager for Offline Journey Support
 *
 * Handles database initialization, migrations, and provides
 * a centralized interface for database operations.
 */
export class DatabaseManager {
  private static instance: DatabaseManager;
  private db: DatabaseInterface | null = null;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): DatabaseManager {
    if (!DatabaseManager.instance) {
      DatabaseManager.instance = new DatabaseManager();
    }
    return DatabaseManager.instance;
  }

  /**
   * Initialize the database and create tables
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Create platform-appropriate database
      this.db = createDatabase();

      // Initialize database
      await this.db.initialize();

      // Enable foreign keys (only for native SQLite)
      if (!isWebPlatform()) {
        await this.db.execAsync('PRAGMA foreign_keys = ON;');
      }

      // Create tables
      if (isWebPlatform()) {
        // On web, create a simplified schema structure
        logger.debug('Web platform: Using simplified schema creation');
        await this.createWebSchema();
      } else {
        // On native, use full SQLite schema
        await this.db.execAsync(CREATE_TABLES_SQL);
      }

      // Run any pending migrations
      await this.runMigrations();

      this.isInitialized = true;
      logger.debug('Database initialized successfully');
    } catch (error) {
      logger.error('Failed to initialize database:', error);
      throw error;
    }
  }

  /**
   * Get the database instance
   */
  public getDatabase(): DatabaseInterface {
    if (!this.db || !this.isInitialized) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    return this.db;
  }

  /**
   * Run database migrations
   */
  private async runMigrations(): Promise<void> {
    if (!this.db) return;

    try {
      if (isWebPlatform()) {
        // On web, migrations are simplified
        logger.debug('Web platform: Using simplified migration system');
        return;
      }

      // Check if migrations table exists
      const result = await this.db.getFirstAsync(
        "SELECT name FROM sqlite_master WHERE type='table' AND name='migrations'"
      );

      if (!result) {
        // Create migrations table
        await this.db.execAsync(`
          CREATE TABLE migrations (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            version TEXT UNIQUE NOT NULL,
            applied_at TEXT NOT NULL
          );
        `);
      }

      // Add any future migrations here
      await this.applyMigration('1.0.0', '-- Initial schema already created');
    } catch (error) {
      logger.error('Migration failed:', error);
      throw error;
    }
  }

  /**
   * Apply a specific migration
   */
  private async applyMigration(version: string, sql: string): Promise<void> {
    if (!this.db) return;

    try {
      if (isWebPlatform()) {
        // On web, migrations are simplified
        logger.debug(
          `Web platform: Migration ${version} handled by IndexedDB schema`
        );
        return;
      }

      // Check if migration already applied
      const existing = await this.db.getFirstAsync(
        'SELECT version FROM migrations WHERE version = ?',
        [version]
      );

      if (existing) {
        return; // Migration already applied
      }

      // Apply migration
      if (sql.trim() !== '-- Initial schema already created') {
        await this.db.execAsync(sql);
      }

      // Record migration
      await this.db.runAsync(
        'INSERT INTO migrations (version, applied_at) VALUES (?, ?)',
        [version, new Date().toISOString()]
      );

      logger.debug(`Migration ${version} applied successfully`);
    } catch (error) {
      logger.error(`Failed to apply migration ${version}:`, error);
      throw error;
    }
  }

  /**
   * Clear all data (for testing or reset)
   */
  public async clearAllData(): Promise<void> {
    if (!this.db) return;

    try {
      if (isWebPlatform()) {
        // On web, clearing data is simplified
        logger.debug('Web platform: Data clearing handled by IndexedDB');
        return;
      }

      await this.db.execAsync(`
        DELETE FROM sync_log;
        DELETE FROM activity_progress;
        DELETE FROM journey_activities;
        DELETE FROM journey_weeks;
        DELETE FROM journeys;
        DELETE FROM content_registry;
      `);
      logger.debug('All data cleared successfully');
    } catch (error) {
      logger.error('Failed to clear data:', error);
      throw error;
    }
  }

  /**
   * Create simplified schema for web platform
   */
  private async createWebSchema(): Promise<void> {
    // On web, we just log that we're using a simplified schema
    // The actual IndexedDB object stores are created in the WebDatabase.initialize() method
    logger.debug(
      'Web platform: Schema creation handled by IndexedDB object stores'
    );
  }

  /**
   * Get database statistics
   */
  public async getStats(): Promise<{
    journeys: number;
    weeks: number;
    activities: number;
    progress: number;
    pendingSync: number;
  }> {
    if (!this.db) {
      return {
        journeys: 0,
        weeks: 0,
        activities: 0,
        progress: 0,
        pendingSync: 0,
      };
    }

    try {
      if (isWebPlatform()) {
        // On web, return simplified stats
        logger.debug('Web platform: Using simplified statistics');
        return {
          journeys: 0,
          weeks: 0,
          activities: 0,
          progress: 0,
          pendingSync: 0,
        };
      }

      const [journeys, weeks, activities, progress, pendingSync] =
        await Promise.all([
          this.db.getFirstAsync('SELECT COUNT(*) as count FROM journeys'),
          this.db.getFirstAsync('SELECT COUNT(*) as count FROM journey_weeks'),
          this.db.getFirstAsync(
            'SELECT COUNT(*) as count FROM journey_activities'
          ),
          this.db.getFirstAsync(
            'SELECT COUNT(*) as count FROM activity_progress'
          ),
          this.db.getFirstAsync(`
          SELECT COUNT(*) as count FROM (
            SELECT id FROM journeys WHERE needs_sync = 1
            UNION ALL
            SELECT id FROM journey_weeks WHERE needs_sync = 1
            UNION ALL
            SELECT id FROM journey_activities WHERE needs_sync = 1
            UNION ALL
            SELECT id FROM activity_progress WHERE needs_sync = 1
          )
        `),
        ]);

      return {
        journeys: (journeys as any)?.count || 0,
        weeks: (weeks as any)?.count || 0,
        activities: (activities as any)?.count || 0,
        progress: (progress as any)?.count || 0,
        pendingSync: (pendingSync as any)?.count || 0,
      };
    } catch (error) {
      logger.error('Failed to get database stats:', error);
      return {
        journeys: 0,
        weeks: 0,
        activities: 0,
        progress: 0,
        pendingSync: 0,
      };
    }
  }

  /**
   * Close the database connection
   */
  public async close(): Promise<void> {
    if (this.db) {
      await this.db.close();
      this.db = null;
      this.isInitialized = false;
    }
  }
}

// Export singleton instance
export const databaseManager = DatabaseManager.getInstance();
